import React, { useState } from "react";
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { withAdminAuth } from "../../HOC";
import { useDeleteQuizMutation, useGetQuizByIdQuery } from "../../Apis/quizApi";
import { ActionButtons } from "../../Compnents/Ui";
import "../../Fonts/font.css";
import { Grid } from "@mui/system";
import { QuizAnalysis } from "../../Compnents/Home";
import QuizComments from "./QuizComments";

export default withAdminAuth(function QuizDetails() {
  const { quizId } = useParams();
  const { data, isLoading, isError } = useGetQuizByIdQuery(quizId);
  const [deleteQuiz] = useDeleteQuizMutation();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [checked, setChecked] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "info" });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // ✅ Moved here so it’s always called, even if data is still loading
  const images = data?.result?.quizImages || [];
  const maxSteps = images.length;

  

  if (isLoading) {
    return (
      <Container sx={{ my: 5, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container sx={{ my: 5 }}>
        <Alert severity="error" sx={{ textAlign: "center" }}>
          Failed to load quiz details.
        </Alert>
      </Container>
    );
  }

  if (!data?.result) {
    return (
      <Container sx={{ my: 5 }}>
        <Alert severity="info" sx={{ textAlign: "center" }}>
          Quiz not found.
        </Alert>
      </Container>
    );
  }

  const Quiz = data.result;

  const handleNext = () => {
    setChecked(false);
    setTimeout(() => {
      setActiveStep((prev) => (prev + 1) % maxSteps);
      setChecked(true);
    }, 300);
  };

  const handleBack = () => {
    setChecked(false);
    setTimeout(() => {
      setActiveStep((prev) => (prev - 1 + maxSteps) % maxSteps);
      setChecked(true);
    }, 300);
  };

  const confirmDelete = async () => {
    if (!selectedId) return;

    try {
      await deleteQuiz({ id: selectedId }).unwrap();

      setSnackbar({
        open: true,
        message: "Quiz Deleted Successfully",
        severity: "success",
      });

      navigate(-1);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.data?.errorMessages?.[0] || "Delete failed",
        severity: "error",
      });
    } finally {
      setOpenDialog(false);
      setSelectedId(null);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ md: 6, xs: 12 }}>
          <Card sx={{ borderRadius: 3, boxShadow: 3, overflow: "hidden" }}>
            <CardHeader
              title={
                <Typography
                  variant="h5"
                  align="center"
                  sx={{ fontWeight: "bold", color: "#512384" }}
                >
                  {Quiz.name}
                </Typography>
              }
            />

            {/* ✅ Slide Section */}
            {maxSteps > 0 && (
              <Box sx={{ position: "relative",  overflow: "hidden" }}>

                <div id="carousel" className="carousel slide"   data-bs-ride="false" data-bs-interval="false" data-bs-theme='dark'>

                  {/* Indicators */}
                  <div className="carousel-indicators">
                    {images.map((img: any, index: number) => (
                      <button
                        key={img.id || index}
                        type="button"
                        data-bs-target="#carousel"
                        data-bs-slide-to={index}
                        className={index === activeStep ? "active" : ""}
                        aria-current={index === activeStep ? "true" : undefined}
                        aria-label={`Slide ${index + 1}`}
                        
                      />
                    ))}
                  </div>

                  {/* Slides */}
                  <div className="carousel-inner">
                    {images.map((img: any, index: number) => (
                      <div
                        key={img.id || index}
                        className={`carousel-item ${index === activeStep ? "active" : ""}`}
                      >
                        <div
                          style={{
                            height: "400px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: "#fafafa",
                          }}
                        >
                          <img
                            src={img.imageUrl}
                            alt={img.captionTitle}
                            style={{
                              maxHeight: "100%",
                              maxWidth: "100%",
                              objectFit: "contain",
                            }}
                            className="d-block"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Controls */}
                  <button
                    className="carousel-control-prev"
                    type="button"
                    onClick={handleBack}
                  >
                    <span className="carousel-control-prev-icon"></span>
                  </button>

                  <button
                    className="carousel-control-next"
                    type="button"
                    onClick={handleNext}
                  >
                    <span className="carousel-control-next-icon"></span>
                  </button>
                </div>

                {/* ✅ Stepper Controls */}
                
              </Box>
            )}

            <CardContent sx={{ pt: 4 }}>
              <Box
                className="m-1"
                dangerouslySetInnerHTML={{ __html: Quiz.description }}
              />
            </CardContent>

            <CardActions sx={{ justifyContent: "flex-end", p: 2 }}>
              <ActionButtons
                onEdit={() => navigate(`/Quiz/Edit/${Quiz.id}`)}
                onDelete={() => {
                  setSelectedId(Quiz.id);
                  setOpenDialog(true);
                }}
                onBack={() => navigate(-1)}
              />
            </CardActions>
          </Card>
        </Grid>
        <Grid size={{ md: 6, xs: 12 }}>
          <QuizComments quizId={quizId} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <QuizAnalysis quizId={quizId} />
        </Grid>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this campaign?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} color="inherit">
              Cancel
            </Button>

            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Box>
  );
});
